/**
 * The searchable surface of every history table, and the token syntax for it.
 *
 * Mirrors `src/shared/history_query.py` and the four per-service registries it
 * serves. Keeping the catalog here rather than fetching it means the filter bar
 * can suggest field names and validate values without a round trip; a parity
 * test (`__tests__/historyQuery.test.ts`) asserts the two lists agree, so the
 * duplication cannot drift silently.
 *
 * Adding a searchable field is one entry here and one line in the matching
 * Python registry.
 */

export type FieldKind = 'text' | 'enum' | 'bool' | 'int' | 'id';

/** The four applications with a history table. */
export type HistoryApp = 'merch' | 'mobile' | 'video' | 'interior';

export const HISTORY_APPS: readonly HistoryApp[] = ['merch', 'mobile', 'video', 'interior'];

export interface HistoryField {
  /** Canonical name, as written in a token. */
  name: string;
  /** Shorter spellings accepted for the same field. */
  aliases: string[];
  kind: FieldKind;
  /** What a person calls this, for the suggestion list. */
  label: string;
  /** Closed vocabulary for `enum`, or the two words a `bool` answers to. */
  values?: string[];
  /** Which applications record it. Others hide it rather than fake it. */
  apps: readonly HistoryApp[];
  /** Searched by a bare word with no field prefix. */
  freeText?: boolean;
  /** Shown under the box as an example when the field is unfamiliar. */
  hint?: string;
}

const ALL: readonly HistoryApp[] = HISTORY_APPS;
/** Applications that record vehicle metadata and generation settings. */
const SCAN_APPS: readonly HistoryApp[] = ['merch', 'mobile'];

export const SOURCE_VALUES = ['manual', 'uvcamp', 'stress', 'benchmark'] as const;
export const TIER_VALUES = ['tier1', 'tier2', 'tier3', 'tier4'] as const;

export const HISTORY_FIELDS: readonly HistoryField[] = [
  { name: 'scan', aliases: ['scan_id'], kind: 'text', label: 'Scan ID', apps: ALL, freeText: true },
  {
    name: 'org',
    aliases: ['organization', 'organization_id'],
    kind: 'text',
    label: 'Organization',
    apps: ALL,
    freeText: true,
  },
  {
    name: 'job',
    aliases: ['job_id'],
    kind: 'id',
    label: 'Job ID',
    apps: ALL,
    hint: 'matches the start of the id, e.g. job:a1b2c3d4',
  },
  { name: 'batch', aliases: ['batch_id'], kind: 'id', label: 'Batch ID', apps: ALL },
  {
    name: 'source',
    aliases: ['src'],
    kind: 'enum',
    label: 'Source',
    values: [...SOURCE_VALUES],
    apps: ALL,
  },
  { name: 'version', aliases: ['ver'], kind: 'text', label: 'Service version', apps: ALL },
  { name: 'make', aliases: [], kind: 'text', label: 'Make', apps: SCAN_APPS, freeText: true },
  { name: 'model', aliases: [], kind: 'text', label: 'Model', apps: SCAN_APPS, freeText: true },
  { name: 'vin', aliases: [], kind: 'text', label: 'VIN', apps: SCAN_APPS, freeText: true },
  { name: 'year', aliases: [], kind: 'int', label: 'Year', apps: SCAN_APPS },
  {
    name: 'tier',
    aliases: [],
    kind: 'enum',
    label: 'Generation tier',
    values: [...TIER_VALUES],
    apps: ['merch'],
    hint: 'scans from before tiers existed count as tier4',
  },
  {
    name: 'photobooth',
    aliases: ['bg', 'background'],
    kind: 'text',
    label: 'Background',
    apps: ['merch', 'mobile', 'interior'],
  },
  {
    name: 'reflection',
    aliases: ['hr'],
    kind: 'bool',
    label: 'High reflectance',
    values: ['yes', 'no'],
    apps: SCAN_APPS,
  },
  {
    name: 'truck',
    aliases: ['pickup'],
    kind: 'bool',
    label: 'Pickup truck',
    values: ['yes', 'no'],
    apps: SCAN_APPS,
  },
  {
    name: 'aftermarket',
    aliases: ['am', 'wheels'],
    kind: 'bool',
    label: 'Aftermarket wheels',
    values: ['yes', 'no'],
    apps: ['merch'],
    hint: 'only recorded on scans finished since this shipped',
  },
  {
    // A boolean that reads better as pass/fail than as yes/no; the Python
    // registry names the same two labels, and both still accept yes/no.
    name: 'overview',
    aliases: ['ov'],
    kind: 'bool',
    label: 'Overview validation',
    values: ['pass', 'fail'],
    apps: ['merch'],
    hint: 'only recorded on scans finished since this shipped',
  },
];

/** The fields *app* records, in catalog order. */
export function fieldsFor(app: HistoryApp): HistoryField[] {
  return HISTORY_FIELDS.filter((field) => field.apps.includes(app));
}

/** Whether *app* records *name*, so the UI can hide a control it cannot use. */
export function hasField(app: HistoryApp, name: string): boolean {
  return fieldsFor(app).some((field) => field.name === name);
}

/** Resolve a token's field name, following aliases. */
export function resolveField(app: HistoryApp, name: string): HistoryField | undefined {
  const wanted = name.trim().toLowerCase();
  return fieldsFor(app).find(
    (field) => field.name === wanted || field.aliases.includes(wanted),
  );
}

export interface StatusOption {
  value: string;
  label: string;
}

/**
 * Terminal statuses, which differ per application: only the scan services can
 * end a job as `cancelled`. The filter bar takes the list from its caller
 * rather than deriving it, since status is not part of the token catalog.
 */
export const SCAN_STATUS_OPTIONS: StatusOption[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const TERMINAL_STATUS_OPTIONS: StatusOption[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

/** The filter parameters shared by every history and export request. */
export interface HistorySearch {
  q?: string | null;
  source?: string | null;
  tier?: string | null;
}

/**
 * Add the search parameters to a request, skipping the empty ones.
 *
 * Every application's API layer needs the same three, and an empty `q=` would
 * otherwise reach the server as a term to parse.
 */
export function applySearchParams(params: URLSearchParams, search?: HistorySearch): void {
  if (!search) return;
  if (search.q) params.set('q', search.q);
  if (search.source) params.set('source', search.source);
  if (search.tier) params.set('tier', search.tier);
}

export interface QueryTerm {
  /** `null` for a bare word, which searches the app's free-text fields. */
  field: string | null;
  value: string;
  negated: boolean;
}

/**
 * Split a query into terms, matching the Python tokenizer.
 *
 * Whitespace separates tokens except inside double quotes, a leading `-` or `!`
 * negates, and a `field:` with nothing after it is dropped rather than reported
 * — the user is mid-word.
 */
export function parseQuery(text: string): QueryTerm[] {
  const tokens: string[] = [];
  let current = '';
  let quoted = false;
  for (const char of text) {
    if (char === '"') {
      quoted = !quoted;
      continue;
    }
    if (/\s/.test(char) && !quoted) {
      if (current) tokens.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  if (current) tokens.push(current);

  const terms: QueryTerm[] = [];
  for (const token of tokens) {
    let body = token;
    let negated = false;
    while (body.startsWith('-') || body.startsWith('!')) {
      negated = true;
      body = body.slice(1);
    }
    if (!body) continue;

    const colon = body.indexOf(':');
    let field: string | null = null;
    let value = body;
    if (colon > 0) {
      field = body.slice(0, colon).toLowerCase();
      value = body.slice(colon + 1);
    }
    value = value.trim();
    if (!value) continue;
    terms.push({ field, value, negated });
  }
  return terms;
}

/** Render terms back to a query string, quoting values that contain spaces. */
export function serializeQuery(terms: readonly QueryTerm[]): string {
  return terms
    .map(({ field, value, negated }) => {
      const quoted = value.includes(' ') ? `"${value}"` : value;
      const prefix = negated ? '-' : '';
      return field ? `${prefix}${field}:${quoted}` : `${prefix}${quoted}`;
    })
    .join(' ');
}

/**
 * Terms naming a field *app* does not record.
 *
 * The backend reports the same list, but computing it here too lets the box
 * warn while the user types instead of only after a request comes back.
 */
export function unknownTerms(app: HistoryApp, text: string): string[] {
  return parseQuery(text)
    .filter((term) => term.field !== null && !resolveField(app, term.field))
    .map((term) => serializeQuery([term]));
}

/**
 * Completions for whatever the user is currently typing.
 *
 * Suggests field names until a colon is typed, then that field's values. Values
 * only exist for `enum` and `bool` fields; the rest are free text, where a
 * suggestion list would just be in the way.
 */
export function suggestQuery(app: HistoryApp, text: string): string[] {
  const active = text.slice(text.lastIndexOf(' ') + 1);
  const bare = active.replace(/^[-!]+/, '');
  const prefix = text.slice(0, text.length - bare.length);

  const colon = bare.indexOf(':');
  if (colon < 0) {
    const typed = bare.toLowerCase();
    return fieldsFor(app)
      .filter((field) => !typed || field.name.startsWith(typed))
      .map((field) => `${prefix}${field.name}:`);
  }

  const field = resolveField(app, bare.slice(0, colon));
  if (!field?.values) return [];
  const typed = bare.slice(colon + 1).toLowerCase();
  return field.values
    .filter((value) => !typed || value.startsWith(typed))
    .map((value) => `${prefix}${field.name}:${value}`);
}
